import React, { useEffect, useState } from 'react';
import PaymentDialog from './PaymentDialog';
import { getAllPayments } from '../services/PaymentService';
import { formatDate, formatUrl } from '../../common/Formatter';
import Dialog from '../../common/components/Dialog';
import { useTranslations } from '../../localization/useTranslations';
import { Payment, Product } from '../types/Payment';
import { getPaymentStatusMessage, formatPaymentAmount, isPaymentFailed, getPaymentType } from '../helpers/PaymentUtils';

const PaymentList = () => {
  // State variables
  const [payments, setPayments] = useState<Payment[] | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showEmptyState, setShowEmptyState] = useState<boolean>(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const translations = useTranslations();
  const myAccountSlug = window.myAccountPageSlug || 'my-account';

  // Effect to fetch payments on component mount
  useEffect(() => {
    fetchPayments();
  }, []);

  // Effect to update empty state based on payments
  useEffect(() => {
    if (payments && payments.length > 0) {
      setShowEmptyState(false);
    }
  }, [payments]);

  // Effect to automatically close snackbar after 6 seconds
  useEffect(() => {
    if (snackbarOpen) {
      setTimeout(() => {
        setSnackbarOpen(false);
      }, 6000);
    }
  }, [snackbarOpen]);

  // Function to fetch payments from the server
  const fetchPayments = async () => {
    try {
      const response = await getAllPayments();

      if (!response) return;

      setPayments(response.payments.length > 0 ? response.payments : null);
    } catch (error) {
      console.error(error);
    }
  };

  // Function to get comma-separated names of payment items
  const getPaymentItems = (items: Array<Product>): string => {
    const itemsNames = items.map((item) => item.name);
    return itemsNames.join(', ');
  };

  // Function to handle payment item click and open the dialog
  const handlePaymentClick = (payment: Payment) => {
    setSelectedPayment(payment);
    setIsDialogOpen(true);
    setTimeout(() => {
      document.documentElement.classList.add('no-scroll');
    }, 100);
  };

  // Function to handle invoice button click and prevent propagation
  const handleInvoiceButtonClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
  };

  // Render the PaymentList component
  return (
    <main className="my-orders">
      <div className="container">
        <div className="my-orders__inner-container">
          <div className="my-orders__section">
            {/* Empty state */}
            {showEmptyState && (
              <div className="empty-state">
                <p className="empty-message">{translations?.emptyStates?.noOrders || "You don't have any orders"}</p>
              </div>
            )}

            {/* Table */}
            {payments && (
              <table className="my-orders__table" cellSpacing={0}>
                <thead>
                  <tr>
                    <th className="table-head basic basic-14">
                      {translations?.orderLabels?.createdDate || 'Created Date'}
                    </th>
                    <th className="table-head basic basic-14">
                      {translations?.orderLabels?.orderNumber || 'Order Number'}
                    </th>
                    <th className="table-head basic basic-14">{translations?.orderLabels?.sum || 'Sum'}</th>
                    <th className="table-head basic basic-14"> {translations?.orderLabels?.status || 'Status'}</th>
                    <th className="table-head basic basic-14">{translations?.orderLabels?.items || 'Items'}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment, index) => (
                    <tr
                      key={index}
                      id="single-order-row"
                      className={`order-row ${isPaymentFailed(payment) ? 'order-row--canceled' : ''}`}
                      data-order-id={index}
                      onClick={() => handlePaymentClick(payment)}
                    >
                      {/* Date Created */}
                      <td className="order-cell order-row__date-created">
                        <span className="order-cell__label basic basic-16"></span>
                        <span className="order-cell__data basic basic-16">{formatDate(payment.created_at)}</span>
                      </td>

                      {/* Order ID */}
                      <td className="order-cell order-row__number">
                        <span className="order-cell__label basic basic-16"></span>
                        <span className="order-cell__data basic basic-16">{payment.id ?? '-'}</span>
                      </td>

                      {/* Sum */}
                      <td className="order-cell order-row__sum">
                        <span className="order-cell__label basic basic-16"></span>
                        <span className="order-cell__data basic basic-16">{formatPaymentAmount(payment)}</span>
                      </td>

                      {/* Status */}
                      <td className="order-cell order-row__status">
                        <span className="order-cell__label basic basic-16"></span>
                        <span className="order-cell__data basic basic-16">
                          {getPaymentStatusMessage(payment, translations)}
                        </span>
                      </td>

                      {/* Items */}
                      <td className="order-cell order-row__items">
                        <span className="order-cell__label basic basic-16"></span>
                        <div className="order-row__items-wrapper">
                          <div className="order-row__item">
                            <span className="order-row__item-label basic basic-16">
                              {payment.products && payment.products.length > 0
                                ? getPaymentItems(payment.products)
                                : (payment.subscription?.label ?? '-')}
                            </span>
                          </div>
                          <div className="order-row__buttons">
                            <div className="order-row__buttons-wrapper">
                              {getPaymentType(payment.sales_funnel) === 'business' && payment.status === 'paid' ? (
                                <a
                                  href={`/${myAccountSlug}/#/${translations?.route?.subscriptions}/`}
                                  className="order-row__code-button order-row__button button button--secondary button--medium"
                                >
                                  {translations?.orderLabels?.getCodesLabel || 'Get the codes'}
                                </a>
                              ) : null}

                              {isPaymentFailed(payment) && payment.retry_url && payment.status !== 'timeout' && (
                                <a
                                  href={payment.retry_url}
                                  className="order-row__retry-button order-row__button button button--secondary button--medium"
                                  target="_blank"
                                  rel="noreferrer noopenner"
                                >
                                  {translations?.orderLabels?.retryPayment || 'Retry payment'}
                                </a>
                              )}

                              {payment && payment.status === 'paid' ? (
                                <a
                                  href={formatUrl(payment.invoice_url)}
                                  rel="noreferrer noopener"
                                  target="_blank"
                                  className="order-row__invoice-button order-row__button button button--secondary button--medium"
                                  onClick={handleInvoiceButtonClick}
                                >
                                  {translations?.orderLabels?.invoice || 'Invoice'}
                                </a>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Render PaymentDialog component if payments and selectedPayment exist */}
      {payments && selectedPayment && (
        <Dialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
          <PaymentDialog
            payment={selectedPayment}
            onClose={() => {
              setIsDialogOpen(false);
              document.documentElement.classList.remove('no-scroll');
            }}
          />
        </Dialog>
      )}
    </main>
  );
};

export default PaymentList;
